import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, CloudDownload, HardDrive, Image, Film, Loader2 } from "lucide-react";
import { formatBytes, darkBtn } from "@/lib/adminApi";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-3 rounded-xl bg-[#FAF8F5] px-4 py-3">
    <Icon className="h-4 w-4 text-[#C5A059]" />
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-400">{label}</p>
      <p data-testid={testid} className="font-display text-lg font-semibold text-stone-900">{value}</p>
    </div>
  </div>
);

export default function MediaStatusBar({ api, items, status, onRefresh }) {
  const [progress, setProgress] = useState(null);
  const running = progress?.running || status?.recovery?.running;

  useEffect(() => {
    if (!running) return;
    const id = setInterval(async () => {
      const r = await api.get("/admin/media/status").catch(() => null);
      if (!r) return;
      setProgress(r.data.recovery);
      if (!r.data.recovery.running) {
        clearInterval(id);
        toast.success(`Récupération terminée : ${r.data.recovery.copied} fichier(s) copié(s), ${r.data.recovery.failed} en échec.`);
        onRefresh();
      }
    }, 2000);
    return () => clearInterval(id);
  }, [running, api, onRefresh]);

  const recover = async () => {
    try {
      const r = await api.post("/admin/media/recover");
      setProgress(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Récupération impossible.");
    }
  };

  const missing = status?.missing?.length || 0;
  const photos = items.filter((m) => m.kind === "photo").length;
  const p = progress || status?.recovery;
  const tried = !running && p?.done > 0 && p?.failed > 0;

  return (
    <section data-testid="media-status" className="mt-8 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat icon={Image} label="Photos" value={photos} testid="media-stat-photos" />
        <Stat icon={Film} label="Vidéos" value={items.length - photos} testid="media-stat-videos" />
        <Stat icon={HardDrive} label="Sur le disque" value={formatBytes(status?.disk_bytes)} testid="media-stat-disk" />
        <Stat icon={missing ? AlertTriangle : CheckCircle2} label="Fichiers manquants" value={missing} testid="media-stat-missing" />
      </div>
      {(missing > 0 || running) && (
        <div data-testid="media-recovery-panel" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/10 px-4 py-3">
          <p className="text-sm text-stone-700">
            {running
              ? `Récupération en cours : ${p?.done || 0} / ${p?.total || 0}…`
              : tried
                ? `${missing} média(s) restent introuvables, même sur l'ancien stockage. Filtrez « Manquants » ci-dessous pour les supprimer.`
                : status?.recoverable
                ? `${missing} média(s) ne sont pas encore sur le disque du serveur. Ils peuvent être récupérés depuis l'ancien stockage.`
                : `${missing} média(s) introuvable(s) sur le disque. ${status?.emergent_error || ""}`}
          </p>
          <button data-testid="media-recover-button" onClick={recover} disabled={running || !status?.recoverable} className={darkBtn}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudDownload className="h-4 w-4" />}
            {running ? "En cours…" : "Récupérer les fichiers"}
          </button>
        </div>
      )}
    </section>
  );
}
